import { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Copy, Check, ArrowLeft, Book, Server, Webhook, Lock, Mail, Code2, AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

/**
 * Public-facing API & Integration Documentation.
 * Shareable URL: /docs/api  (and /docs/integrations)
 *
 * No auth required to view — intended for sharing with partners (ScoutsEye,
 * BabyLoveGrowth.ai, SAGE Scholars, GameSense, 6Tool, college conferences, etc.)
 *
 * The actual Bearer token is NOT shown on this page — it's exchanged privately.
 */
export default function ApiDocsPage() {
  const sections = [
    { id: "overview", label: "Overview" },
    { id: "tier-policy", label: "Cross-Platform Tiers" },
    { id: "auth", label: "Authentication" },
    { id: "blog-ingest", label: "Blog Ingest" },
    { id: "scoutseye", label: "ScoutsEye Sync" },
    { id: "babylovegrowth", label: "BabyLoveGrowth" },
    { id: "player-directory", label: "Player Directory" },
    { id: "share-grants", label: "Share Grants" },
    { id: "support", label: "Support" },
    { id: "sso", label: "SSO (Tier-Signed)" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="api-docs-page">
      {/* Header */}
      <header className="border-b border-white/10 bg-gradient-to-b from-black/40 to-transparent">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center text-xs text-white/50 hover:text-white" data-testid="docs-back-home">
            <ArrowLeft size={12} className="mr-1.5" /> Back to NSB
          </Link>
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold">Patent Pending</div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[10px] uppercase tracking-[0.25em] font-bold mb-4">
            <Server size={11} /> Partner Integration Docs
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3">NSB Integration API</h1>
          <p className="text-base sm:text-lg text-white/60 max-w-2xl">
            Webhook receivers, partner sync endpoints, and player directory access for authorized National Scouting Bureau partners.
          </p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex gap-10">
        {/* Sticky TOC */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-6 space-y-1" data-testid="docs-toc">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">Contents</div>
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="block text-sm text-white/60 hover:text-amber-300 py-1.5 px-3 rounded-md hover:bg-white/[0.03] transition">
                {s.label}
              </a>
            ))}
          </div>
        </aside>

        {/* Body */}
        <article className="flex-1 min-w-0 space-y-14">
          {/* Overview */}
          <section id="overview">
            <SectionHeader icon={Book} title="Overview" />
            <p className="text-white/70 leading-relaxed">
              The NSB Integration API is a token-protected REST interface that lets approved partners exchange data with the National Scouting Bureau platform. It powers player profile sync with ScoutsEye, AI-assisted article ingestion from BabyLoveGrowth.ai, and cross-platform identity propagation across SAGE Scholars, GameSense, Demotu, 6Tool, and Quality At-Bats.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              <BaseUrlCard env="Production" url="https://nationalscoutingbureau.com" highlight />
              <BaseUrlCard env="Preview / Sandbox" url="https://nsb-recruitment-hub.preview.emergentagent.com" />
            </div>
            <p className="text-xs text-white/40 mt-3">All endpoints are prefixed with <code className="text-amber-300">/api</code>. JSON only. UTF-8.</p>
          </section>

          {/* Auth */}
          <section id="tier-policy">
            <SectionHeader icon={Shield} title="Cross-Platform Tier Policy" />
            <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] via-transparent to-orange-500/[0.04] p-5 mb-5">
              <p className="text-white/85 leading-relaxed">
                Automatic premium access on NSB based on cross-platform login is <strong className="text-amber-300">null and void</strong>.
                NSB access is gated <strong className="text-white">strictly by the user's NSB tier</strong>:
                <code className="text-amber-300 ml-1">freemium</code> · <code className="text-amber-300">plus</code> · <code className="text-amber-300">elite</code> · <code className="text-amber-300">platinum</code>.
              </p>
            </div>
            <ul className="space-y-3 text-sm text-white/75 leading-relaxed list-disc pl-5">
              <li>
                <strong className="text-white">NSB currently fully supports baseball only.</strong> If a CEi/HBCUi-originating user signs up at NSB declaring a non-baseball sport, they remain on <code className="text-amber-300">freemium</code> and see only CEi/HBCUi tier offerings — not NSB paid tier upsells.
              </li>
              <li>
                <strong className="text-white">Paid NSB tiers (plus / elite / platinum) unlock full CEi / HBCUi access.</strong> When an NSB paid user SSOs to a partner platform, NSB signs a JWT containing <code className="text-amber-300">tier</code>, <code className="text-amber-300">paid: true</code>, and <code className="text-amber-300">sport</code> — partners must trust this claim and grant full access.
              </li>
              <li>
                Inbound free-tier users from CEi/HBCUi default to <code className="text-amber-300">freemium</code> + <code className="text-amber-300">source_platform: "cei"</code> (or <code className="text-amber-300">"hbcui"</code>) and can browse public NSB content only.
              </li>
            </ul>
            <p className="text-xs text-white/40 mt-4">
              This is a <strong>shared policy</strong>. Any change must be coordinated and reflected in all partner codebases.
            </p>
          </section>

          {/* Auth */}
          <section id="auth">
            <SectionHeader icon={Lock} title="Authentication" />
            <p className="text-white/70 mb-4">
              All integration endpoints require a partner-specific <strong className="text-white">Bearer token</strong>. Tokens are issued by NSB on partnership approval and exchanged out-of-band. <strong>Do not embed your token in client-side code.</strong>
            </p>
            <Code title="Required Headers">
{`Authorization: Bearer <YOUR_NSB_PARTNER_TOKEN>
Content-Type: application/json`}
            </Code>
            <p className="text-xs text-white/40 mt-3">
              The <code className="text-amber-300">X-API-Key</code> header is also accepted as a fallback for legacy partners.
            </p>
            <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-4 text-sm text-amber-100/80 flex gap-3">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-amber-400" />
              <p>
                Need a token? Email <a href="mailto:partnerships@nationalscoutingbureau.com" className="text-amber-300 font-semibold underline">partnerships@nationalscoutingbureau.com</a> with your organization, intended integration, and engineering contact.
              </p>
            </div>
          </section>

          {/* Blog Ingest */}
          <section id="blog-ingest">
            <SectionHeader icon={Webhook} title="Blog Ingest (BabyLoveGrowth.ai-style)" />
            <p className="text-white/70 mb-4">
              Push articles directly into the NSB ScoutWire News widget. Articles default to the <code className="text-amber-300">Inside NSB</code> division. Repeated calls with the same <code className="text-amber-300">slug</code> update in place (idempotent).
            </p>
            <Endpoint method="POST" path="/api/integrations/blog/ingest" />
            <Code title="Request Body">
{`{
  "title": "How to Share Performance Reports Digitally for Recruitment",
  "content": "<p>Full HTML article body…</p>",
  "slug": "how-to-share-performance-reports-digitally-for-recruitment",
  "excerpt": "Optional summary",
  "category": "news",
  "tags": ["recruiting", "reports"],
  "featured_image_url": "https://cdn.example.com/hero.jpg"
}`}
            </Code>
            <Code title="Response 200">
{`{
  "success": true,
  "action": "created",
  "post_id": "post_a1b2c3d4e5f6",
  "slug": "how-to-share-performance-reports-digitally-for-recruitment",
  "post": { ... }
}`}
            </Code>
            <p className="text-xs text-white/40 mt-3">
              Articles are publicly viewable at <code className="text-amber-300">https://nationalscoutingbureau.com/blog/&#123;slug&#125;</code>
            </p>
          </section>

          {/* ScoutsEye */}
          <section id="scoutseye">
            <SectionHeader icon={Server} title="ScoutsEye ↔ NSB Player Sync" />
            <p className="text-white/70 mb-5">
              Bi-directional player profile sync. NSB is the source of truth; ScoutsEye-discovered players land as <code className="text-amber-300">claim_status: "unclaimed"</code> until claimed by the player/parent.
            </p>

            <Endpoint method="POST" path="/api/integrations/scoutseye/webhook/player" />
            <p className="text-white/60 text-sm mb-3">Real-time receiver: ScoutsEye fires this whenever a scout creates or updates a player. Matches existing NSB profiles by <code className="text-amber-300">external_ids.scoutseye</code> → email → normalized name+school.</p>
            <Code title="Webhook Payload">
{`{
  "player_id": "plyr_scoutseye_xxx",
  "full_name": "Test Player",
  "school": "Test High School",
  "primary_position": "SS",
  "class_year": "JR",
  "bats": "R",
  "throws": "R",
  "height": "6-0",
  "weight": 175,
  "hometown": "Test City, FL",
  "photo_url": "https://cdn.example.com/p.jpg",
  "email": "player@optional.com"
}`}
            </Code>
            <Code title="Response 200">
{`{
  "success": true,
  "action": "linked_or_updated",
  "nsb_player_id": "athlete_xxxxxxxxxxxx",
  "scoutseye_id": "plyr_scoutseye_xxx"
}`}
            </Code>

            <div className="mt-8">
              <Endpoint method="POST" path="/api/integrations/scoutseye/sync?direction=pull|push|both&limit=100" />
              <p className="text-white/60 text-sm">
                Token-protected sync trigger for cron jobs / external schedulers. NSB also runs an automatic background poller every 60 minutes.
              </p>
            </div>
          </section>

          {/* BabyLoveGrowth */}
          <section id="babylovegrowth">
            <SectionHeader icon={Server} title="BabyLoveGrowth Pull Sync" />
            <p className="text-white/70 mb-4">
              Trigger an immediate pull from BabyLoveGrowth's article API into NSB. NSB also polls automatically every 30 minutes.
            </p>
            <Endpoint method="POST" path="/api/integrations/babylovegrowth/sync?limit=25" />
            <Code title="Response 200">
{`{
  "success": true,
  "fetched": 2,
  "created": 1,
  "updated": 1,
  "results": [ ... ]
}`}
            </Code>
          </section>

          {/* Player Directory */}
          <section id="player-directory">
            <SectionHeader icon={Server} title="Player Directory (read)" />
            <p className="text-white/70 mb-4">
              Token-protected read access to NSB player records. Used by ScoutsEye and approved scouting platforms.
            </p>
            <Endpoint method="GET" path="/api/integrations/nsb/players?limit=100" />
            <Endpoint method="GET" path="/api/integrations/nsb/players/{athlete_id_or_scoutseye_id}" />
            <p className="text-xs text-white/40 mt-3">
              The detail endpoint accepts either an NSB <code className="text-amber-300">athlete_id</code> or a linked <code className="text-amber-300">external_ids.scoutseye</code> value — useful for reverse lookups.
            </p>
          </section>

          {/* Share Grants */}
          <section id="share-grants">
            <SectionHeader icon={Webhook} title="Share Grants (athlete → coach)" />
            <p className="text-white/70 mb-4">
              Players issue one-time signed magic links from their Player Dashboard. Recipients open them at <code className="text-amber-300">/share/&#123;token&#125;</code> without authentication — possession of the token == authorization.
            </p>
            <Endpoint method="GET" path="/api/share/{token}" />
            <Code title="Response 200">
{`{
  "share": {
    "share_id": "share_xxxxxxxxxxxxxx",
    "coach_email": "coach@university.edu",
    "coach_name": "Coach Smith",
    "coach_school": "LSU",
    "personal_note": "...",
    "athlete_name": "..."
  },
  "player": { ...full private scouting record... }
}`}
            </Code>
            <p className="text-xs text-white/40 mt-3">
              Links remain active until the athlete explicitly revokes them. View counts and last-viewed timestamps are tracked on every fetch.
            </p>
          </section>

          {/* SSO */}
          <section id="sso">
            <SectionHeader icon={Lock} title="SSO (Tier-Signed Redirects)" />
            <p className="text-white/70 mb-4">
              When an authenticated NSB user clicks a partner link inside the Player Dashboard, NSB issues a short-lived (5 min) signed JWT and appends it to the redirect URL as <code className="text-amber-300">?nsb_token=&lt;jwt&gt;</code>. Partners verify the signature against the shared <code className="text-amber-300">JWT_SECRET</code> and read the tier to gate platform access.
            </p>
            <Endpoint method="GET" path="/api/sso/launch/{partner}" />
            <Code title="Response 200">
{`{
  "success": true,
  "partner": "college-ed-insights",
  "token": "<JWT>",
  "tier": "elite",
  "paid": true,
  "sport": "baseball",
  "ttl_seconds": 300
}`}
            </Code>
            <Code title="Decoded JWT Payload">
{`{
  "t":          "nsb_sso",
  "iss":        "nsb",
  "sub":        "user_xxxxxxxxxxxx",
  "email":      "athlete@example.com",
  "name":       "Xan Harwell",
  "tier":       "elite",
  "nsb_member": true,
  "paid":       true,
  "sport":      "baseball",
  "partner":    "college-ed-insights",
  "iat":        1717612345,
  "exp":        1717612645
}`}
            </Code>
            <div className="mt-4 rounded-xl border border-blue-500/30 bg-blue-500/[0.05] p-4 text-sm text-blue-100/85 flex gap-3">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-blue-400" />
              <p>
                <strong>Partner enforcement requirement:</strong> if <code className="text-amber-300">paid: true</code>, grant the user full platform access. If <code className="text-amber-300">paid: false</code>, show your own free / paid tier offerings — do NOT auto-upgrade. This is non-negotiable per the cross-platform tier policy.
              </p>
            </div>
          </section>

          {/* Support */}
          <section id="support">
            <SectionHeader icon={Mail} title="Support & Partnership" />
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Partnership Inquiries</div>
                <a href="mailto:partnerships@nationalscoutingbureau.com" className="text-amber-300 hover:text-amber-200 font-semibold">partnerships@nationalscoutingbureau.com</a>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Engineering / Integration Support</div>
                <a href="mailto:rod@scoutseyeapp.com" className="text-amber-300 hover:text-amber-200 font-semibold">rod@scoutseyeapp.com</a>
              </div>
              <p className="text-xs text-white/40 pt-2 border-t border-white/5">
                Production endpoints are rate-limited per partner. If you anticipate sustained high volume (&gt; 500 req/min), please coordinate with NSB engineering ahead of integration.
              </p>
            </div>
          </section>

          <footer className="border-t border-white/10 pt-6 pb-12 mt-12 text-xs text-white/30">
            <p>NSB Integration API · National Scouting Bureau, LLC · Patent Pending.</p>
            <p className="mt-1">NSB Verified&trade;, NSB Fusion&trade;, and Scholar-Athlete Operating System&trade; are trademarks of National Scouting Bureau, LLC.</p>
          </footer>
        </article>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <h2 className="text-2xl font-black tracking-tight text-white mb-4 flex items-center gap-2.5">
      <span className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
        <Icon size={15} className="text-amber-400" />
      </span>
      {title}
    </h2>
  );
}

function BaseUrlCard({ env, url, highlight }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-amber-500/40 bg-amber-500/[0.05]" : "border-white/10 bg-white/[0.03]"}`}>
      <div className="flex items-center justify-between mb-1">
        <div className={`text-[10px] uppercase tracking-wider font-bold ${highlight ? "text-amber-300" : "text-white/40"}`}>{env}</div>
        <button onClick={copy} className="text-white/40 hover:text-amber-300" data-testid={`copy-url-${env.toLowerCase().replace(/\s+/g, '-')}`}>
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
      <code className="text-sm text-white font-mono break-all">{url}</code>
    </div>
  );
}

function Endpoint({ method, path }) {
  const colors = { GET: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", POST: "bg-blue-500/20 text-blue-300 border-blue-500/30", PUT: "bg-amber-500/20 text-amber-300 border-amber-500/30", DELETE: "bg-red-500/20 text-red-300 border-red-500/30" };
  const cls = colors[method] || "bg-white/10 text-white/70 border-white/20";
  return (
    <div className="flex items-center gap-3 mb-3 flex-wrap">
      <span className={`px-2.5 py-1 rounded-md border text-[11px] font-bold ${cls}`}>{method}</span>
      <code className="text-sm text-white font-mono">{path}</code>
    </div>
  );
}

function Code({ title, children }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(children); setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden mb-4">
      <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
        <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold flex items-center gap-1.5"><Code2 size={10} /> {title}</div>
        <button onClick={copy} className="text-white/40 hover:text-amber-300" data-testid={`copy-${title.toLowerCase().replace(/\s+/g, '-')}`}>
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
      <pre className="px-4 py-3 text-xs sm:text-[13px] text-white/85 font-mono overflow-x-auto leading-relaxed"><code>{children}</code></pre>
    </div>
  );
}
